import { useCallback, useEffect, useRef, useState } from "react";

type DeviceMotionEventCtor = typeof DeviceMotionEvent & {
  requestPermission?: () => Promise<"granted" | "denied">;
};

type Options = {
  /** 加速度の閾値 (m/s² 程度のスケール) */
  threshold?: number;
  /** 完了とする蓄積カウント */
  goal?: number;
  /** タップでも進められるか */
  acceptTap?: boolean;
};

/**
 * useShake — 鈴を振る検出。
 *  - DeviceMotion (加速度) が使えるなら振動量で進行
 *  - 使えない (PCブラウザ) ならタップ連打でも進行 (acceptTap)
 *  - intensity は 0..1 に正規化
 */
export function useShake({ threshold = 12, goal = 8, acceptTap = true }: Options = {}) {
  const [intensity, setIntensity] = useState(0);
  const [completed, setCompleted] = useState(false);
  const countRef = useRef(0);
  const lastShakeAt = useRef(0);

  const handleProgress = useCallback(() => {
    countRef.current += 1;
    const next = Math.min(1, countRef.current / goal);
    setIntensity(next);
    if (next >= 1 && !completed) setCompleted(true);
  }, [goal, completed]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof DeviceMotionEvent === "undefined") return;
    const ctor = DeviceMotionEvent as DeviceMotionEventCtor;
    if (typeof ctor.requestPermission === "function") return; // iOS — caller must request

    const handler = (e: DeviceMotionEvent) => {
      const a = e.accelerationIncludingGravity;
      if (!a) return;
      const mag = Math.hypot(a.x ?? 0, a.y ?? 0, a.z ?? 0);
      const now = performance.now();
      if (mag > threshold && now - lastShakeAt.current > 200) {
        lastShakeAt.current = now;
        handleProgress();
      }
    };
    window.addEventListener("devicemotion", handler);
    return () => window.removeEventListener("devicemotion", handler);
  }, [threshold, handleProgress]);

  const requestPermission = useCallback(async () => {
    const ctor = DeviceMotionEvent as DeviceMotionEventCtor | undefined;
    if (!ctor || typeof ctor.requestPermission !== "function") return "granted" as const;
    try {
      return await ctor.requestPermission();
    } catch {
      return "denied" as const;
    }
  }, []);

  /** タップで1カウント進める (PC等のフォールバック) */
  const tap = useCallback(() => {
    if (!acceptTap) return;
    handleProgress();
  }, [acceptTap, handleProgress]);

  const reset = useCallback(() => {
    countRef.current = 0;
    setIntensity(0);
    setCompleted(false);
  }, []);

  return { intensity, completed, tap, reset, requestPermission };
}
